import { useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  GET_PREDICTIVE_MOD_CONFIG,
  GET_PREDICTIVE_MOD_STATUS,
  GET_PREDICTIVE_MOD_LOGS,
  TOGGLE_PREDICTIVE_MOD_CONTROL,
  UPDATE_PREDICTIVE_MOD_CONFIG,
  CHECK_PREDICTIVE_MOD_STATE,
} from '../graphql/predictiveModOperations';

// ==================== TYPES ====================

export interface PredictiveModControlConfig {
  id: string;
  enabled: boolean;
  checkIntervalSeconds: number;
  threshold100Probability: number;
  threshold90Probability: number;
  threshold70Probability: number;
  threshold50Probability: number;
  thresholdBelowProbability: number;
  lastCheckAt: Date | null;
  lastModState: boolean | null;
  lastPercentage: number | null;
  lastProbability: number | null;
  lastDecision: string | null;
  lastReason: string | null;
  lastChangedBy: string | null;
  lastChangedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface PredictiveModControlStatus {
  enabled: boolean;
  modEnabled: boolean;
  currentTaxAmount: number;
  currentTaxTarget: number;
  currentPercentage: number;
  probabilityToActivate: number;
  thresholdLevel: string;
  lastCheckAt: Date | null;
  lastDecision: string | null;
  lastReason: string | null;
  checkIntervalSeconds: number;
}

export interface PredictiveModControlLog {
  id: string;
  action: string;
  previousModState: boolean | null;
  newModState: boolean | null;
  taxPercentage: number | null;
  probabilityUsed: number | null;
  randomValue: number | null;
  decision: string | null;
  reason: string | null;
  changedBy: string | null;
  createdAt: Date;
}

export interface PredictiveCheckResult {
  changed: boolean;
  previousState: boolean;
  newState: boolean;
  percentage: number;
  probability: number;
  randomValue: number;
  decision: string;
  reason: string;
}

export interface UpdatePredictiveModConfigInput {
  checkIntervalSeconds?: number;
  threshold100Probability?: number;
  threshold90Probability?: number;
  threshold70Probability?: number;
  threshold50Probability?: number;
  thresholdBelowProbability?: number;
  changedBy?: string;
}

// ==================== HOOKS ====================

/**
 * Hook para obtener la configuración del control predictivo
 */
export const usePredictiveModConfig = () => {
  const { data, loading, error, refetch } = useQuery<{
    getPredictiveModControlConfig: PredictiveModControlConfig;
  }>(GET_PREDICTIVE_MOD_CONFIG, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    config: data?.getPredictiveModControlConfig,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para obtener el estado actual del control predictivo
 * @param pollInterval - Intervalo de polling en ms (default: 3000 = 3 segundos)
 */
export const usePredictiveModStatus = (pollInterval: number = 3000) => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    getPredictiveModControlStatus: PredictiveModControlStatus;
  }>(GET_PREDICTIVE_MOD_STATUS, {
    fetchPolicy: 'network-only',
    pollInterval: pollInterval,
  });

  return {
    status: data?.getPredictiveModControlStatus,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

/**
 * Hook para obtener los logs del control predictivo
 */
export const usePredictiveModLogs = (limit: number = 50) => {
  const { data, loading, error, refetch } = useQuery<{
    getPredictiveModControlLogs: PredictiveModControlLog[];
  }>(GET_PREDICTIVE_MOD_LOGS, {
    variables: { limit },
    fetchPolicy: 'cache-and-network',
  });

  return {
    logs: data?.getPredictiveModControlLogs || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para activar/desactivar el control predictivo
 */
export const useTogglePredictiveModControl = () => {
  const [toggleMutation, { loading }] = useMutation(TOGGLE_PREDICTIVE_MOD_CONTROL, {
    refetchQueries: [
      { query: GET_PREDICTIVE_MOD_CONFIG },
      { query: GET_PREDICTIVE_MOD_STATUS },
    ],
  });

  const toggle = useCallback(
    async (enabled: boolean, changedBy: string) => {
      const result = await toggleMutation({
        variables: { enabled, changedBy },
      });
      return result.data?.togglePredictiveModControl;
    },
    [toggleMutation]
  );

  return {
    toggle,
    toggling: loading,
  };
};

/**
 * Hook para actualizar la configuración del control predictivo
 */
export const useUpdatePredictiveModConfig = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_PREDICTIVE_MOD_CONFIG, {
    refetchQueries: [
      { query: GET_PREDICTIVE_MOD_CONFIG },
      { query: GET_PREDICTIVE_MOD_STATUS },
    ],
  });

  const updateConfig = useCallback(
    async (input: UpdatePredictiveModConfigInput) => {
      const result = await updateMutation({
        variables: { input },
      });
      return result.data?.updatePredictiveModConfig;
    },
    [updateMutation]
  );

  return {
    updateConfig,
    updating: loading,
  };
};

/**
 * Hook para ejecutar una verificación manual
 */
export const useCheckPredictiveModState = () => {
  const [checkMutation, { loading }] = useMutation(CHECK_PREDICTIVE_MOD_STATE, {
    refetchQueries: [
      { query: GET_PREDICTIVE_MOD_STATUS },
      { query: GET_PREDICTIVE_MOD_LOGS },
    ],
  });

  const check = useCallback(async () => {
    const result = await checkMutation();
    return result.data?.checkPredictiveModState as PredictiveCheckResult;
  }, [checkMutation]);

  return {
    check,
    checking: loading,
  };
};
